/**
 * Phone number utility functions for formatting, validation, and input handling
 */

/**
 * Format phone number for display with dashes
 * @param {string} phone - Raw phone number
 * @returns {string} - Formatted phone number
 */
export const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format based on length
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  if (cleaned.length <= 10) return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  
  // For numbers longer than 10 digits, format as: 1-XXX-XXX-XXXX
  return `${cleaned.slice(0, 1)}-${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
};

/**
 * Format phone number for storage (remove all formatting)
 * @param {string} phone - Formatted phone number
 * @returns {string} - Clean phone number for storage
 */
export const formatPhoneForStorage = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Validate phone number format
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid format
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return true; // Empty is valid (optional field)
  
  const cleaned = phone.replace(/\D/g, '');
  // Allow 7 digits (local), 10 digits (US), or 11 digits (with country code)
  return cleaned.length >= 7 && cleaned.length <= 11;
};

/**
 * Handle key press events for phone number fields
 * Only allow digits, dashes, and navigation keys
 * @param {KeyboardEvent} event - The keyboard event
 */
export const handlePhoneKeyPress = (event) => {
  const allowedKeys = [
    'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Tab', 'Enter', 'Escape', 'Home', 'End'
  ];
  
  const isDigit = /[0-9]/.test(event.key);
  const isDash = event.key === '-';
  const isAllowedKey = allowedKeys.includes(event.key);
  
  // Allow Ctrl/Cmd combinations for copy/paste/select all
  if (event.ctrlKey || event.metaKey) {
    return;
  }
  
  if (!isDigit && !isDash && !isAllowedKey) {
    event.preventDefault();
  }
};

/**
 * Handle phone number input change with automatic formatting
 * @param {string} value - Current input value
 * @param {Function} setValue - Function to set the new value
 * @param {string} fieldName - Name of the field being updated
 * @param {Function} setFormData - Function to update form data
 */
export const handlePhoneChange = (value, setValue, fieldName, setFormData) => {
  const formatted = formatPhoneForDisplay(value);
  
  if (setValue) {
    setValue(formatted);
  }
  
  if (setFormData && fieldName) {
    setFormData(prev => ({
      ...prev,
      [fieldName]: formatted
    }));
  }
};
